import type { RiskLevel } from '@trajectory/contracts';
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import { useMemo } from 'react';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useRocketPerformance } from '@/hooks/useRockets';
import { computeAnalytics } from '@/lib/analytics';
import { useRocketStore } from '@/stores/rocketStore';
import { useTelemetryStore } from '@/stores/telemetryStore';

const RISK_COLOR: Record<RiskLevel, string> = {
  low: 'text-success',
  moderate: 'text-warning',
  elevated: 'text-warning',
  high: 'text-danger',
};

const SEVERITY_ICON = { info: Info, warning: AlertTriangle, error: XCircle };

export function AnalyticsPanel() {
  const rocketId = useRocketStore((state) => state.id);
  const rocketDocument = useRocketStore((state) => state.document);
  const performance = useRocketPerformance(rocketId);
  const summary = useTelemetryStore((state) => state.summary);

  const analytics = useMemo(() => {
    if (!rocketDocument || !performance.data) return null;
    return computeAnalytics(
      rocketDocument,
      performance.data,
      summary
        ? {
            deltaVExpendedMetersPerSecond: summary.deltaVExpended,
            reachedOrbit: summary.reachedOrbit,
            reachedDestination: summary.reachedDestination,
            destinationBodyId: summary.destinationBodyId,
            terminationReason: summary.terminationReason,
          }
        : null,
    );
  }, [rocketDocument, performance.data, summary]);

  if (!rocketId) {
    return <div className="flex h-full items-center justify-center p-4 text-center text-xs text-faint">Select a mission with a rocket assigned.</div>;
  }

  if (performance.isError) {
    return (
      <div role="alert" className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-xs">
        <p className="text-danger">Could not compute vehicle analytics.</p>
        <p className="text-faint">
          {performance.error instanceof Error ? performance.error.message : String(performance.error)}
        </p>
        <button
          type="button"
          onClick={() => void performance.refetch()}
          className="rounded-control border border-border px-2 py-1 text-muted hover:text-text"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return <div className="flex h-full items-center justify-center p-4 text-xs text-faint">Computing…</div>;
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-3 text-sm">
      <PanelLearningBanner panelId="analytics" />
      <p className="rounded-control border border-info/30 bg-info/5 px-2 py-1.5 text-[11px] text-muted">
        Scores and cost figures are comparative screening estimates. Use the physical telemetry,
        assumptions, and independent validation for academic conclusions or engineering decisions.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-panel border border-border bg-surface-raised p-3 text-center">
          <p className="text-2xl font-semibold text-text">{analytics.performanceScore}</p>
          <p className="text-xs text-muted">Performance score</p>
        </div>
        <div className="rounded-panel border border-border bg-surface-raised p-3 text-center">
          <p className={`text-2xl font-semibold capitalize ${RISK_COLOR[analytics.riskLevel]}`}>{analytics.riskLevel}</p>
          <p className="text-xs text-muted">Risk level</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-panel border border-border bg-surface-raised p-3 text-xs text-muted">
        <span><TermHelp termId="margin">Delta-V margin</TermHelp></span>
        <span className={`text-right ${analytics.deltaVMarginMetersPerSecond >= 0 ? 'text-success' : 'text-danger'}`}>
          {analytics.deltaVMarginMetersPerSecond >= 0 ? '+' : ''}
          {analytics.deltaVMarginMetersPerSecond.toFixed(0)} m/s
        </span>
        <span>Payload efficiency</span>
        <span className="text-right text-text">{(analytics.payloadEfficiency * 100).toFixed(2)}%</span>
        <span>Fuel efficiency</span>
        <span className="text-right text-text">{(analytics.fuelEfficiency * 1000).toFixed(2)} kg payload / t propellant</span>
        <span>Launch success probability</span>
        <span className="text-right text-text">{(analytics.launchSuccessProbability * 100).toFixed(1)}%</span>
        <span>Estimated cost</span>
        <span className="text-right text-text">${(analytics.estimatedCostUsd / 1e6).toFixed(1)}M</span>
        <span>Cost per kg to orbit</span>
        <span className="text-right text-text">${analytics.costPerKilogramUsd.toFixed(0)}/kg</span>
      </div>

      {summary?.physicsModel && (
        <div className="rounded-panel border border-border bg-surface-raised p-3 text-xs">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="font-medium text-text">Propagation fidelity</span>
            <span className="rounded-full bg-accent-soft px-2 py-0.5 text-accent">
              {summary.physicsModel === 'numerical-perturbed'
                ? 'Numerical perturbed'
                : 'Ideal Kepler reference'}
            </span>
          </div>
          <p className="text-muted">
            {(summary.forceModel ?? []).join(' · ')}
          </p>
          <p className="mt-1 text-faint">
            {summary.steps.toLocaleString()} integration steps
          </p>
        </div>
      )}

      <div>
        <h3 className="mb-1 text-xs font-medium text-muted">Constraint violations</h3>
        {analytics.violations.length === 0 && <p className="text-xs text-success">None — vehicle clears every check.</p>}
        <div className="flex flex-col gap-1">
          {analytics.violations.map((violation, index) => {
            const Icon = SEVERITY_ICON[violation.severity];
            const color = violation.severity === 'error' ? 'text-danger' : violation.severity === 'warning' ? 'text-warning' : 'text-info';
            return (
              <div key={`${violation.code}-${index}`} className="flex items-start gap-2 rounded-control border border-border px-2 py-1.5 text-xs">
                <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${color}`} />
                <span className="text-muted">{violation.message}</span>
              </div>
            );
          })}
        </div>
      </div>

      {!summary && (
        <p className="text-xs text-faint">Delta-v margin uses a standard ~9.4 km/s LEO budget estimate — run a simulation for a measured figure.</p>
      )}
    </div>
  );
}
